import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Calculator, Clock, CheckCircle, AlertCircle, Eye, Upload, 
  Car, Calendar, User, FileText, ArrowLeft, Save, Download,
  RefreshCw, Search, Filter, X, Image, ExternalLink, Building2,
  MapPin, Phone, Mail, Shield, Wrench, Euro, Maximize2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminKalkulationen = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [kalkulationen, setKalkulationen] = useState([]);
  const [stats, setStats] = useState({ total: 0, eingereicht: 0, in_bearbeitung: 0, abgeschlossen: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedKalkulation, setSelectedKalkulation] = useState(null);
  const [selectedAkte, setSelectedAkte] = useState(null);
  const [filterStatus, setFilterStatus] = useState('wartend');
  const [ergebnisForm, setErgebnisForm] = useState({
    reparaturkosten_netto: '',
    reparaturkosten_brutto: '',
    arbeitskosten: '',
    ersatzteilkosten: '',
    lackierkosten: '',
    reparaturdauer_stunden: '',
    reparaturdauer_tage: '',
    wiederbeschaffungswert: '',
    restwert: '',
    minderwert: '',
    mietwagenkosten_pro_tag: '',
    nutzungsausfall_pro_tag: '',
    totalschaden: false,
    wirtschaftlicher_totalschaden: false,
    bemerkungen: ''
  });

  // DAT myClaim States
  const [datTab, setDatTab] = useState('manuell');
  const [datLoading, setDatLoading] = useState(false);
  const [datIframeUrl, setDatIframeUrl] = useState(null);
  const [datClaimId, setDatClaimId] = useState(null);
  const [datFullscreen, setDatFullscreen] = useState(false);
  const datIframeRef = useRef(null);

  const getToken = () => localStorage.getItem('autogutachter24_token');

  useEffect(() => {
    if (id) {
      loadKalkulationDetail(id);
    } else {
      loadKalkulationen();
    }
  }, [id, filterStatus]);

  const loadKalkulationen = async () => {
    setLoading(true);
    try {
      const endpoint = filterStatus === 'wartend' 
        ? '/api/kalkulation/admin/warteschlange'
        : '/api/kalkulation/admin/alle';
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (!response.ok) throw new Error('Fehler beim Laden');
      
      const data = await response.json();
      setKalkulationen(data.kalkulationen || []);
      if (data.stats) setStats(data.stats);
    } catch (error) {
      toast.error('Fehler beim Laden der Kalkulationen');
    } finally {
      setLoading(false);
    }
  };

  const loadKalkulationDetail = async (kalkulationId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/kalkulation/admin/detail/${kalkulationId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (!response.ok) throw new Error('Fehler beim Laden');
      
      const data = await response.json();
      setSelectedKalkulation(data.kalkulation);
      setSelectedAkte(data.akte);
      
      if (data.kalkulation.ergebnis) {
        setErgebnisForm(prev => ({ ...prev, ...data.kalkulation.ergebnis }));
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Details');
      navigate('/admin/kalkulationen');
    } finally {
      setLoading(false);
    }
  };

  const startBearbeitung = async (kalkulationId) => {
    try {
      const response = await fetch(`${API_URL}/api/kalkulation/admin/bearbeiten/${kalkulationId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (!response.ok) throw new Error('Fehler');
      
      toast.success('Bearbeitung gestartet');
      loadKalkulationDetail(kalkulationId);
    } catch (error) {
      toast.error('Fehler beim Starten der Bearbeitung');
    }
  };

  const uploadPdf = async (kalkulationId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/kalkulation/admin/upload-pdf/${kalkulationId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formData
      });
      
      if (!response.ok) throw new Error('Upload fehlgeschlagen');
      
      toast.success('PDF hochgeladen');
      loadKalkulationDetail(kalkulationId);
    } catch (error) {
      toast.error('Fehler beim Upload');
    }
  };

  const abschliessenKalkulation = async () => {
    if (!selectedKalkulation) return;
    
    try {
      const ergebnis = {
        ...ergebnisForm,
        reparaturkosten_netto: ergebnisForm.reparaturkosten_netto ? parseFloat(ergebnisForm.reparaturkosten_netto) : null,
        reparaturkosten_brutto: ergebnisForm.reparaturkosten_brutto ? parseFloat(ergebnisForm.reparaturkosten_brutto) : null,
        arbeitskosten: ergebnisForm.arbeitskosten ? parseFloat(ergebnisForm.arbeitskosten) : null,
        ersatzteilkosten: ergebnisForm.ersatzteilkosten ? parseFloat(ergebnisForm.ersatzteilkosten) : null,
        lackierkosten: ergebnisForm.lackierkosten ? parseFloat(ergebnisForm.lackierkosten) : null,
        reparaturdauer_stunden: ergebnisForm.reparaturdauer_stunden ? parseFloat(ergebnisForm.reparaturdauer_stunden) : null,
        reparaturdauer_tage: ergebnisForm.reparaturdauer_tage ? parseInt(ergebnisForm.reparaturdauer_tage) : null,
        wiederbeschaffungswert: ergebnisForm.wiederbeschaffungswert ? parseFloat(ergebnisForm.wiederbeschaffungswert) : null,
        restwert: ergebnisForm.restwert ? parseFloat(ergebnisForm.restwert) : null,
        minderwert: ergebnisForm.minderwert ? parseFloat(ergebnisForm.minderwert) : null,
        mietwagenkosten_pro_tag: ergebnisForm.mietwagenkosten_pro_tag ? parseFloat(ergebnisForm.mietwagenkosten_pro_tag) : null,
        nutzungsausfall_pro_tag: ergebnisForm.nutzungsausfall_pro_tag ? parseFloat(ergebnisForm.nutzungsausfall_pro_tag) : null,
      };
      
      const response = await fetch(`${API_URL}/api/kalkulation/admin/abschliessen/${selectedKalkulation.id}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ergebnis)
      });
      
      if (!response.ok) throw new Error('Fehler beim Abschließen');
      
      toast.success('Kalkulation abgeschlossen!');
      navigate('/admin/kalkulationen');
    } catch (error) {
      toast.error('Fehler beim Abschließen der Kalkulation');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'eingereicht': { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Wartend' },
      'in_bearbeitung': { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'In Bearbeitung' },
      'abgeschlossen': { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Abgeschlossen' }
    };
    const badge = badges[status] || badges['eingereicht'];
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // DAT myClaim Contract erstellen
  const createDatContract = async () => {
    if (!selectedAkte) return;
    
    setDatLoading(true);
    try {
      // Bereite Fahrzeugdaten vor
      const contractData = {
        vehicle: {
          manufacturer: selectedAkte.marke || '',
          baseModel: selectedAkte.modell || '',
          subModel: selectedAkte.typ || '',
          vin: selectedAkte.vin || '',
          licensePlate: selectedAkte.kennzeichen || '',
          firstRegistration: selectedAkte.erstzulassung || '',
          mileage: parseInt(selectedAkte.kilometerstand) || 0,
          paintType: selectedAkte.lackart || 'UNI',
          numberOfPreviousOwners: 1
        },
        claim: {
          claimNumber: selectedAkte.aktenzeichen || selectedKalkulation.aktenzeichen || '',
          dateOfLoss: selectedAkte.schadentag || '',
          damageDescription: selectedAkte.schadenbeschreibung || '',
          repairType: 'VRO'
        },
        customer: {
          name: selectedAkte.kunde || '',
          street: selectedAkte.adresse1 || '',
          city: selectedAkte.adresse2 || '',
          phone: selectedAkte.telefon || '',
          email: selectedAkte.email || ''
        }
      };
      
      const response = await fetch(`${API_URL}/api/dat/contract`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          contractData,
          contractType: 'vro_calculation',
          networkType: 'DAT'
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setDatClaimId(data.claimId);
        setDatIframeUrl(data.iframeUrl);
        toast.success('DAT myClaim gestartet');
      } else {
        toast.error(data.error || 'Fehler beim Starten von DAT myClaim');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    } finally {
      setDatLoading(false);
    }
  };

  // Kalkulation von DAT abrufen
  const fetchDatKalkulation = async () => {
    if (!datClaimId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/dat/contract/${datClaimId}/kalkulation`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      const data = await response.json();
      
      if (data.success && data.kalkulation) {
        // Übertrage DAT-Ergebnisse in das Formular
        setErgebnisForm(prev => ({
          ...prev,
          reparaturkosten_netto: data.kalkulation.reparaturkosten_netto || '',
          reparaturkosten_brutto: data.kalkulation.reparaturkosten_brutto || '',
          arbeitskosten: data.kalkulation.arbeitskosten || '',
          ersatzteilkosten: data.kalkulation.ersatzteilkosten || '',
          lackierkosten: data.kalkulation.lackierkosten || '',
          reparaturdauer_stunden: data.kalkulation.reparaturdauer_stunden || '',
          wiederbeschaffungswert: data.kalkulation.wiederbeschaffungswert || '',
          minderwert: data.kalkulation.minderwert || ''
        }));
        toast.success('DAT Kalkulation übernommen!');
      } else {
        toast.info('Noch keine Kalkulationsdaten verfügbar');
      }
    } catch (error) {
      toast.error('Fehler beim Abrufen der DAT Kalkulation');
    }
  };

  // Detail-Ansicht
  if (selectedKalkulation && selectedAkte) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                onClick={() => { setSelectedKalkulation(null); setSelectedAkte(null); navigate('/admin/kalkulationen'); }}
                className="text-gray-500 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">{selectedKalkulation.aktenzeichen}</h1>
                <p className="text-gray-500">{selectedKalkulation.fahrzeug_marke} {selectedKalkulation.fahrzeug_modell}</p>
              </div>
              {getStatusBadge(selectedKalkulation.status)}
            </div>
            
            <div className="flex space-x-3">
              {selectedKalkulation.status === 'eingereicht' && (
                <Button onClick={() => startBearbeitung(selectedKalkulation.id)} className="bg-blue-600 hover:bg-blue-700">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Bearbeitung starten
                </Button>
              )}
              {selectedKalkulation.status === 'in_bearbeitung' && (
                <Button onClick={abschliessenKalkulation} className="bg-green-600 hover:bg-green-700">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Abschließen
                </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Linke Spalte: Akte-Daten */}
            <div className="space-y-6">
              {/* Fahrzeugdaten - Erweitert */}
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="border-b border-gray-200 bg-blue-50">
                  <CardTitle className="text-gray-800 flex items-center">
                    <Car className="w-5 h-5 mr-2 text-blue-600" />
                    Fahrzeugdaten
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Marke/Modell</span>
                      <p className="text-gray-800 font-semibold">{selectedAkte?.marke} {selectedAkte?.modell}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Kennzeichen</span>
                      <p className="text-gray-800 font-semibold font-mono bg-yellow-100 px-2 py-1 rounded inline-block">{selectedAkte?.kennzeichen || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Erstzulassung</span>
                      <p className="text-gray-800 font-medium">{selectedAkte?.erstzulassung || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Kilometerstand</span>
                      <p className="text-gray-800 font-medium">{selectedAkte?.kilometerstand ? `${parseInt(selectedAkte.kilometerstand).toLocaleString('de-DE')} km` : '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">HSN / TSN</span>
                      <p className="text-gray-800 font-mono text-xs">{selectedAkte?.hsn || '-'} / {selectedAkte?.tsn || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Farbe</span>
                      <p className="text-gray-800 font-medium">{selectedAkte?.farbe || '-'}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">VIN</span>
                      <p className="text-gray-800 font-mono text-xs bg-gray-100 px-2 py-1 rounded">{selectedAkte?.vin || '-'}</p>
                    </div>
                    {selectedAkte?.hubraum && (
                      <div>
                        <span className="text-gray-500">Hubraum</span>
                        <p className="text-gray-800 font-medium">{selectedAkte.hubraum} ccm</p>
                      </div>
                    )}
                    {selectedAkte?.leistung_kw && (
                      <div>
                        <span className="text-gray-500">Leistung</span>
                        <p className="text-gray-800 font-medium">{selectedAkte.leistung_kw} kW / {Math.round(selectedAkte.leistung_kw * 1.36)} PS</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Schadensinformationen */}
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="border-b border-gray-200 bg-red-50">
                  <CardTitle className="text-gray-800 flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
                    Schadensinformationen
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Schadentag</span>
                      <p className="text-gray-800 font-medium">{selectedAkte?.schadentag || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Schadenort</span>
                      <p className="text-gray-800 font-medium">{selectedAkte?.schadenort || '-'}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">Schadenhergang</span>
                      <p className="text-gray-800 text-sm mt-1 bg-gray-50 p-3 rounded">{selectedAkte?.schadenhergang || selectedAkte?.schadenbeschreibung || 'Keine Beschreibung'}</p>
                    </div>
                    {selectedAkte?.besichtigungsdatum && (
                      <div>
                        <span className="text-gray-500">Besichtigung</span>
                        <p className="text-gray-800 font-medium">{selectedAkte.besichtigungsdatum}</p>
                      </div>
                    )}
                    {selectedAkte?.besichtigungsort && (
                      <div>
                        <span className="text-gray-500">Besichtigungsort</span>
                        <p className="text-gray-800 font-medium">{selectedAkte.besichtigungsort}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Auftraggeber */}
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="border-b border-gray-200 bg-purple-50">
                  <CardTitle className="text-gray-800 flex items-center">
                    <User className="w-5 h-5 mr-2 text-purple-600" />
                    Auftraggeber (Geschädigter)
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="col-span-2">
                      <span className="text-gray-500">Name</span>
                      <p className="text-gray-800 font-semibold text-lg">{selectedAkte?.kunde || '-'}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">Adresse</span>
                      <p className="text-gray-800">{selectedAkte?.adresse1 || '-'}</p>
                      <p className="text-gray-800">{selectedAkte?.adresse2 || ''}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" /> Telefon</span>
                      <p className="text-gray-800">{selectedAkte?.telefon || selectedAkte?.mobilnummer || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500 flex items-center gap-1"><Mail className="w-3 h-3" /> E-Mail</span>
                      <p className="text-gray-800 text-sm">{selectedAkte?.email || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Gegnerische Versicherung */}
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="border-b border-gray-200 bg-orange-50">
                  <CardTitle className="text-gray-800 flex items-center">
                    <Shield className="w-5 h-5 mr-2 text-orange-600" />
                    Gegnerische Versicherung
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="col-span-2">
                      <span className="text-gray-500">Versicherung</span>
                      <p className="text-gray-800 font-semibold">{selectedAkte?.versicherung_gesellschaft || selectedAkte?.versicherungsname || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Schadennummer</span>
                      <p className="text-gray-800 font-mono">{selectedAkte?.schadennummer || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Versicherungsscheinnr.</span>
                      <p className="text-gray-800 font-mono">{selectedAkte?.versicherung_nummer || selectedAkte?.versicherungsscheinnummer || '-'}</p>
                    </div>
                    {selectedAkte?.gegner_name && (
                      <>
                        <div className="col-span-2 border-t pt-3 mt-2">
                          <span className="text-gray-500">Unfallgegner</span>
                          <p className="text-gray-800 font-medium">{selectedAkte.gegner_name}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Adresse</span>
                          <p className="text-gray-800 text-sm">{selectedAkte.gegner_strasse}, {selectedAkte.gegner_ort}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Kennzeichen</span>
                          <p className="text-gray-800 font-mono">{selectedAkte.gegner_kennzeichen || '-'}</p>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Bilder */}
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="border-b border-gray-200">
                  <CardTitle className="text-gray-800 flex items-center">
                    <Image className="w-5 h-5 mr-2 text-green-600" />
                    Lichtbilder ({selectedAkte?.lichtbilder?.length || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {selectedAkte?.lichtbilder?.length > 0 ? (
                    <div className="grid grid-cols-4 gap-2">
                      {selectedAkte.lichtbilder.map((bild, idx) => (
                        <div key={idx} className="aspect-square rounded-lg overflow-hidden border border-gray-200 hover:border-blue-400 transition-colors">
                          <img 
                            src={bild.url || bild.thumbnail_url || bild} 
                            alt={`Bild ${idx + 1}`}
                            className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                            onClick={() => window.open(bild.url || bild, '_blank')}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">Keine Bilder vorhanden</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Rechte Spalte: DAT Kalkulation */}
            <div className="space-y-6">
              {/* DAT myClaim Integration */}
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700">
                  <CardTitle className="text-white flex items-center justify-between">
                    <div className="flex items-center">
                      <Calculator className="w-5 h-5 mr-2" />
                      Kalkulation
                    </div>
                    <Tabs value={datTab} onValueChange={setDatTab} className="w-auto">
                      <TabsList className="bg-blue-800/50">
                        <TabsTrigger value="dat" className="text-white data-[state=active]:bg-white data-[state=active]:text-blue-700 text-xs">
                          DAT myClaim
                        </TabsTrigger>
                        <TabsTrigger value="manuell" className="text-white data-[state=active]:bg-white data-[state=active]:text-blue-700 text-xs">
                          Manuell
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {/* DAT myClaim Tab */}
                  {datTab === 'dat' && (
                    <div className="p-6">
                      {!datIframeUrl ? (
                        <div className="text-center py-8">
                          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Calculator className="w-10 h-10 text-blue-600" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-800 mb-2">DAT myClaim Kalkulation</h3>
                          <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                            Starten Sie die professionelle Schadenkalkulation mit DAT myClaim. 
                            Die Fahrzeugdaten werden automatisch übertragen.
                          </p>
                          <Button 
                            onClick={createDatContract}
                            disabled={datLoading}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-8"
                          >
                            {datLoading ? (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Wird gestartet...
                              </>
                            ) : (
                              <>
                                <ExternalLink className="w-4 h-4 mr-2" />
                                DAT myClaim starten
                              </>
                            )}
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between bg-green-50 p-3 rounded-lg">
                            <div className="flex items-center gap-2 text-green-700">
                              <CheckCircle className="w-5 h-5" />
                              <span className="font-medium">DAT myClaim aktiv</span>
                              <span className="text-xs bg-green-200 px-2 py-0.5 rounded">ID: {datClaimId}</span>
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => setDatFullscreen(true)}
                                className="text-blue-600"
                              >
                                <Maximize2 className="w-4 h-4 mr-1" />
                                Vollbild
                              </Button>
                              <Button 
                                size="sm"
                                onClick={fetchDatKalkulation}
                                className="bg-green-600 hover:bg-green-700 text-white"
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Daten übernehmen
                              </Button>
                            </div>
                          </div>
                          
                          {/* DAT iframe */}
                          <div className="border border-gray-200 rounded-lg overflow-hidden" style={{ height: '600px' }}>
                            <iframe
                              ref={datIframeRef}
                              src={datIframeUrl}
                              className="w-full h-full"
                              title="DAT myClaim"
                              allow="fullscreen"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Manuell Tab */}
                  {datTab === 'manuell' && (
                    <div className="p-6 space-y-6">
                      {/* PDF Upload */}
                      <div>
                        <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                          <Upload className="w-4 h-4 text-gray-500" />
                          Kalkulations-PDF
                        </h4>
                        {selectedKalkulation.pdf_url ? (
                          <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center space-x-3">
                              <FileText className="w-8 h-8 text-green-600" />
                              <div>
                                <p className="font-medium text-gray-800">{selectedKalkulation.pdf_filename}</p>
                                <p className="text-sm text-gray-500">PDF hochgeladen</p>
                              </div>
                            </div>
                            <Button 
                              variant="outline"
                              className="border-green-500 text-green-600 hover:bg-green-50"
                              onClick={() => window.open(`${API_URL}${selectedKalkulation.pdf_url}`, '_blank')}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download
                            </Button>
                          </div>
                        ) : (
                          <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-blue-400 transition-colors">
                            <input
                              type="file"
                              accept=".pdf"
                              className="hidden"
                              id="pdf-upload"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  uploadPdf(selectedKalkulation.id, e.target.files[0]);
                                }
                              }}
                            />
                            <label htmlFor="pdf-upload" className="cursor-pointer">
                              <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                              <p className="font-medium text-gray-600">Kalkulations-PDF hochladen</p>
                              <p className="text-sm text-gray-400 mt-1">Klicken oder Datei hierher ziehen</p>
                            </label>
                          </div>
                        )}
                      </div>

                      {/* Reparaturkosten */}
                      <div>
                        <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                          <Wrench className="w-4 h-4 text-gray-500" />
                          Reparaturkosten
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Netto (€)</label>
                            <Input
                              type="number"
                              value={ergebnisForm.reparaturkosten_netto}
                              onChange={(e) => setErgebnisForm(prev => ({ ...prev, reparaturkosten_netto: e.target.value }))}
                              placeholder="0.00"
                              className="border-gray-300"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Brutto (€)</label>
                            <Input
                              type="number"
                              value={ergebnisForm.reparaturkosten_brutto}
                              onChange={(e) => setErgebnisForm(prev => ({ ...prev, reparaturkosten_brutto: e.target.value }))}
                              placeholder="0.00"
                              className="border-gray-300"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Arbeitskosten (€)</label>
                            <Input
                              type="number"
                              value={ergebnisForm.arbeitskosten}
                              onChange={(e) => setErgebnisForm(prev => ({ ...prev, arbeitskosten: e.target.value }))}
                              placeholder="0.00"
                              className="border-gray-300"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Ersatzteile (€)</label>
                            <Input
                              type="number"
                              value={ergebnisForm.ersatzteilkosten}
                              onChange={(e) => setErgebnisForm(prev => ({ ...prev, ersatzteilkosten: e.target.value }))}
                              placeholder="0.00"
                              className="border-gray-300"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Lackierkosten (€)</label>
                            <Input
                              type="number"
                              value={ergebnisForm.lackierkosten}
                              onChange={(e) => setErgebnisForm(prev => ({ ...prev, lackierkosten: e.target.value }))}
                              placeholder="0.00"
                              className="border-gray-300"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Reparaturdauer (Std.)</label>
                            <Input
                              type="number"
                              value={ergebnisForm.reparaturdauer_stunden}
                              onChange={(e) => setErgebnisForm(prev => ({ ...prev, reparaturdauer_stunden: e.target.value }))}
                              placeholder="0"
                              className="border-gray-300"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Werte */}
                      <div>
                        <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                          <Euro className="w-4 h-4 text-gray-500" />
                          Fahrzeugwerte
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Wiederbeschaffungswert (€)</label>
                            <Input
                              type="number"
                              value={ergebnisForm.wiederbeschaffungswert}
                              onChange={(e) => setErgebnisForm(prev => ({ ...prev, wiederbeschaffungswert: e.target.value }))}
                              placeholder="0.00"
                              className="border-gray-300"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Restwert (€)</label>
                            <Input
                              type="number"
                              value={ergebnisForm.restwert}
                              onChange={(e) => setErgebnisForm(prev => ({ ...prev, restwert: e.target.value }))}
                              placeholder="0.00"
                              className="border-gray-300"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Minderwert (€)</label>
                            <Input
                              type="number"
                              value={ergebnisForm.minderwert}
                              onChange={(e) => setErgebnisForm(prev => ({ ...prev, minderwert: e.target.value }))}
                              placeholder="0.00"
                              className="border-gray-300"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Reparaturdauer (Tage)</label>
                            <Input
                              type="number"
                              value={ergebnisForm.reparaturdauer_tage}
                              onChange={(e) => setErgebnisForm(prev => ({ ...prev, reparaturdauer_tage: e.target.value }))}
                              placeholder="0"
                              className="border-gray-300"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Mietwagen / Nutzungsausfall */}
                      <div>
                        <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                          <Car className="w-4 h-4 text-gray-500" />
                          Ausfallkosten
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Mietwagen €/Tag</label>
                            <Input
                              type="number"
                              value={ergebnisForm.mietwagenkosten_pro_tag}
                              onChange={(e) => setErgebnisForm(prev => ({ ...prev, mietwagenkosten_pro_tag: e.target.value }))}
                              placeholder="0.00"
                              className="border-gray-300"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Nutzungsausfall €/Tag</label>
                            <Input
                              type="number"
                              value={ergebnisForm.nutzungsausfall_pro_tag}
                              onChange={(e) => setErgebnisForm(prev => ({ ...prev, nutzungsausfall_pro_tag: e.target.value }))}
                              placeholder="0.00"
                              className="border-gray-300"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Totalschaden */}
                      <div className="flex items-center gap-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={ergebnisForm.totalschaden}
                            onChange={(e) => setErgebnisForm(prev => ({ ...prev, totalschaden: e.target.checked }))}
                            className="w-4 h-4 text-red-600 rounded"
                          />
                          <span className="text-sm text-gray-700">Totalschaden</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={ergebnisForm.wirtschaftlicher_totalschaden}
                            onChange={(e) => setErgebnisForm(prev => ({ ...prev, wirtschaftlicher_totalschaden: e.target.checked }))}
                            className="w-4 h-4 text-orange-600 rounded"
                          />
                          <span className="text-sm text-gray-700">Wirtschaftlicher Totalschaden</span>
                        </label>
                      </div>

                      {/* Bemerkungen */}
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Bemerkungen</label>
                        <Textarea
                          value={ergebnisForm.bemerkungen}
                          onChange={(e) => setErgebnisForm(prev => ({ ...prev, bemerkungen: e.target.value }))}
                          placeholder="Zusätzliche Anmerkungen..."
                          rows={3}
                          className="border-gray-300"
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* DAT Fullscreen Modal */}
          {datFullscreen && datIframeUrl && (
            <div className="fixed inset-0 bg-black/80 z-50 flex flex-col">
              <div className="bg-white p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Calculator className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold">DAT myClaim - {selectedKalkulation.aktenzeichen}</span>
                </div>
                <div className="flex gap-2">
                  <Button onClick={fetchDatKalkulation} className="bg-green-600 hover:bg-green-700 text-white">
                    <Download className="w-4 h-4 mr-2" />
                    Daten übernehmen
                  </Button>
                  <Button variant="outline" onClick={() => setDatFullscreen(false)}>
                    <X className="w-4 h-4 mr-2" />
                    Schließen
                  </Button>
                </div>
              </div>
              <div className="flex-1">
                <iframe
                  src={datIframeUrl}
                  className="w-full h-full"
                  title="DAT myClaim Fullscreen"
                />
              </div>
            </div>
          )}
        </div>
      </AdminLayout>
    );
  }

  // Listen-Ansicht
  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="bg-yellow-500/10 border-yellow-500/30">
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <Clock className="w-8 h-8 text-yellow-400" />
                <div>
                  <p className="text-2xl font-bold text-yellow-400">{stats.eingereicht}</p>
                  <p className="text-sm text-yellow-300/70">Wartend</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <RefreshCw className="w-8 h-8 text-blue-400" />
                <div>
                  <p className="text-2xl font-bold text-blue-400">{stats.in_bearbeitung}</p>
                  <p className="text-sm text-blue-300/70">In Bearbeitung</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-8 h-8 text-green-400" />
                <div>
                  <p className="text-2xl font-bold text-green-400">{stats.abgeschlossen}</p>
                  <p className="text-sm text-green-300/70">Abgeschlossen</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-500/10 border-gray-500/30">
            <CardContent className="p-4">
              <div className="flex items-center space-x-3">
                <Calculator className="w-8 h-8 text-gray-500" />
                <div>
                  <p className="text-2xl font-bold text-gray-600">{stats.total}</p>
                  <p className="text-sm text-gray-500">Gesamt</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex space-x-2">
          <Button 
            variant={filterStatus === 'wartend' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('wartend')}
            className={filterStatus === 'wartend' ? 'bg-orange-600' : 'border-gray-600 text-gray-600'}
          >
            Warteschlange
          </Button>
          <Button 
            variant={filterStatus === 'alle' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('alle')}
            className={filterStatus === 'alle' ? 'bg-orange-600' : 'border-gray-600 text-gray-600'}
          >
            Alle anzeigen
          </Button>
        </div>

        {/* Tabelle */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="w-8 h-8 text-gray-500 animate-spin mx-auto mb-3" />
                <p className="text-gray-500">Laden...</p>
              </div>
            ) : kalkulationen.length === 0 ? (
              <div className="p-8 text-center">
                <Calculator className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500">Keine Kalkulationen vorhanden</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Aktenzeichen</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Fahrzeug</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Gutachter</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Eingereicht</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Bilder</th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Aktion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {kalkulationen.map((kalk) => (
                    <tr key={kalk.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <span className="font-medium text-gray-800">{kalk.aktenzeichen}</span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-800">{kalk.fahrzeug_marke} {kalk.fahrzeug_modell}</p>
                        <p className="text-xs text-gray-500">{kalk.kennzeichen}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{kalk.user_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatDate(kalk.eingereicht_am)}</td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 rounded-full text-xs bg-gray-700 text-gray-600">
                          {kalk.bilder_count} Bilder
                        </span>
                      </td>
                      <td className="px-6 py-4">{getStatusBadge(kalk.status)}</td>
                      <td className="px-6 py-4 text-right">
                        <Button 
                          size="sm" 
                          onClick={() => navigate(`/admin/kalkulationen/${kalk.id}`)}
                          className="bg-orange-600 hover:bg-orange-700"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Bearbeiten
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminKalkulationen;
